// Shared shape produced by every parser (file-based or vision-based).
// The commit step consumes only this shape.

export type LifecycleStatus = 'active' | 'completed' | 'cancelled' | 'on_hold';

export interface NormalizedAssignee {
  externalId: string;
  name: string;
  email?: string;
  avatarUrl?: string;
  role?: string;
}

export interface NormalizedTask {
  externalId: string;
  parentExternalId?: string | null;
  name: string;
  intent?: string;
  specs?: string[];
  assigneeExternalIds?: string[];
  startDate?: string; // ISO
  endDate?: string;   // ISO
  status?: string;          // raw source status
  lifecycle?: LifecycleStatus;
  progress?: number;        // 0-100
  flagged?: boolean;
  comments?: { author?: string; body: string; at?: string }[];
  attachments?: { url: string; name?: string }[];
  tags?: string[];
  customFields?: Record<string, string>;
  /**
   * Stable sibling sequence seeded at import time (see src/lib/wbs/wrikeSeed.ts).
   * Drives computed WBS numbering; never embedded in `name`.
   */
  sequence?: number | null;
  /** Controlled workstream identity seeded from source metadata, not title text. */
  workstreamKey?: string | null;
  sourceImageRef?: string;  // capture session image path that produced this
  confidence?: number;      // 0-1
  // Source-system hierarchy metadata. Captured at parse time so the UI can
  // recompute Task vs Subtask labeling later even after we flatten nesting
  // to fit Workitect's 2-level model. Keys vary by source platform but
  // typically include: originalParentKind ('Task'|'Folder'|'Space'|'Project'),
  // originalDepth (number), originalParentExternalId, flattenedFromExternalId.
  importMetadata?: Record<string, unknown>;
}

export interface NormalizedDependency {
  fromExternalId: string;
  toExternalId: string;
}

export interface NormalizedSettings {
  workWeek?: number[]; // 0=Sun..6=Sat
  timezone?: string;
  customFields?: { name: string; type: string }[];
  tags?: string[];
  themeAccent?: string;
}

export interface NormalizedImport {
  sourcePlatform: string;
  projects: { externalId: string; name: string; description?: string }[];
  tasks: NormalizedTask[];
  dependencies: NormalizedDependency[];
  assignees: NormalizedAssignee[];
  settings?: NormalizedSettings;
  warnings: string[];
}

export const emptyNormalized = (sourcePlatform: string): NormalizedImport => ({
  sourcePlatform,
  projects: [],
  tasks: [],
  dependencies: [],
  assignees: [],
  warnings: [],
});
